var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, ToggleButton, styled, } from '@mui/material';
import QRCode from 'qrcode.react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';
import { MFAType, MFAValidationType } from '../../enum';
import { useUserSessionAPI } from '../app/selectors';
import { Autocomplete, ErrorMessageBox, FallbackComponent, FormField, PhoneInput } from '../common';
import { MFAOptions, MFAValidationOptions, validateForm } from './constants';
import { useChangeMFAMethod, useGetPhoneNumber, useGetSecretCodeAPI, useHasConfiguredAuthenticator, useMFAConfigAPI, } from './selectors';
import { DeviceMode } from './types';
import { VerifyPhoneNumber } from './verifyPhoneNumber';
var StyledToggleButton = styled(ToggleButton)(function (_a) {
    var theme = _a.theme;
    return ({
        border: 'none',
        background: 'none',
        textDecoration: 'underline',
        color: theme.palette.primary.main,
        '&.Mui-selected, &:hover, &.Mui-selected:hover': {
            background: 'none',
            textDecoration: 'none',
        },
        '&.Mui-selected': {
            color: theme.palette.text.primary,
        },
    });
});
export var SettingMFA = function (_a) {
    var open = _a.open, onClose = _a.onClose;
    var _b = useState(false), openVerifyPhoneNumber = _b[0], setOpenVerifyPhoneNumber = _b[1];
    var userData = useUserSessionAPI()[0].data;
    var _c = useMFAConfigAPI(), mfaConfig = _c[0], fetchMFAConfig = _c[1], clearMFAConfig = _c[2];
    var _d = useChangeMFAMethod(), changeMFAMethod = _d[0], fetchChangeMFAMethod = _d[1], clearChangeMFAMethod = _d[2];
    var _e = useGetSecretCodeAPI(), secretCode = _e[0], fetchSecretCode = _e[1], clearSecretCode = _e[2];
    var hasConfiguredAuthenticator = useHasConfiguredAuthenticator();
    var phoneNumberFromSetting = useGetPhoneNumber();
    var t = useTranslation('translation').t;
    var defaultValues = useMemo(function () {
        var _a, _b, _c;
        return ({
            type: (_b = (_a = mfaConfig.data) === null || _a === void 0 ? void 0 : _a.PreferredMfaSetting) !== null && _b !== void 0 ? _b : MFAType.TOTP,
            validation: MFAValidationType.QRCode,
            secretKey: (_c = secretCode.data) === null || _c === void 0 ? void 0 : _c.SecretCode,
            deviceMode: !mfaConfig.data || hasConfiguredAuthenticator ? undefined : DeviceMode.New,
            userCode: '',
            friendlyDeviceName: '',
            phone: phoneNumberFromSetting,
        });
    }, [hasConfiguredAuthenticator, mfaConfig.data, phoneNumberFromSetting, secretCode.data]);
    var _f = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(validateForm),
    }), control = _f.control, watch = _f.watch, reset = _f.reset, handleSubmit = _f.handleSubmit, setValue = _f.setValue;
    var _g = watch(), type = _g.type, validation = _g.validation, secretKey = _g.secretKey, deviceMode = _g.deviceMode, phone = _g.phone;
    useEffect(function () {
        if (open) {
            fetchMFAConfig();
            fetchSecretCode();
            return function () {
                clearMFAConfig();
                clearSecretCode();
                clearChangeMFAMethod();
            };
        }
    }, [clearChangeMFAMethod, clearMFAConfig, clearSecretCode, fetchMFAConfig, fetchSecretCode, open]);
    useEffect(function () {
        reset(defaultValues);
    }, [defaultValues, reset]);
    useEffect(function () {
        var _a;
        if ((_a = changeMFAMethod.data) === null || _a === void 0 ? void 0 : _a.verifyPhoneRequired) {
            setOpenVerifyPhoneNumber(true);
        }
        else if (changeMFAMethod.data) {
            onClose();
        }
    }, [changeMFAMethod.data, onClose]);
    var handleChangeDeviceMode = function (mode) {
        setValue('deviceMode', mode !== deviceMode ? mode : undefined);
    };
    var handleFormSubmit = handleSubmit(function (values) {
        var userCode = values.userCode, friendlyDeviceName = values.friendlyDeviceName, mfaType = values.type, phoneNumber = values.phone;
        if (mfaType === MFAType.SMS) {
            fetchChangeMFAMethod({ phone: phoneNumber });
        }
        else if (mfaType === MFAType.TOTP && values.deviceMode) {
            fetchChangeMFAMethod({ userCode: userCode, friendlyDeviceName: friendlyDeviceName });
        }
        else {
            fetchChangeMFAMethod();
        }
    });
    return (_jsxs(Dialog, __assign({ open: open, onClose: onClose }, { children: [_jsxs(Box, __assign({ component: "form", onSubmit: handleFormSubmit }, { children: [_jsx(DialogTitle, __assign({ variant: "h2" }, { children: t('layout.settingMFA') })), _jsxs(FallbackComponent, __assign({ requesting: !!mfaConfig.requesting || !!secretCode.requesting || !!changeMFAMethod.requesting, overlay: true }, { children: [_jsxs(DialogContent, { children: [_jsx(ErrorMessageBox, { errors: [mfaConfig.error, secretCode.error, changeMFAMethod.error] }), _jsxs(Grid, __assign({ container: true, spacing: 6 }, { children: [_jsxs(Grid, __assign({ item: true, xs: 12 }, { children: [_jsx(FormField, __assign({ name: "type", label: t('mfa.form.method'), control: control }, { children: _jsx(Autocomplete, { options: MFAOptions }) })), _jsx(Stack, __assign({ direction: "row", gap: 3 }, { children: type === MFAType.TOTP && hasConfiguredAuthenticator && (_jsxs(_Fragment, { children: [_jsx(StyledToggleButton, __assign({ value: "check", size: "small", color: "primary", selected: deviceMode === DeviceMode.New, onChange: function () { return handleChangeDeviceMode(DeviceMode.New); } }, { children: t('mfa.form.changeDevice') })), _jsx(StyledToggleButton, __assign({ value: "check", size: "small", color: "primary", selected: deviceMode === DeviceMode.ChangeName, onChange: function () { return handleChangeDeviceMode(DeviceMode.ChangeName); } }, { children: t('mfa.form.updateDeviceName') }))] })) }))] })), type === MFAType.TOTP && !!deviceMode && (_jsxs(_Fragment, { children: [deviceMode === DeviceMode.New && (_jsxs(_Fragment, { children: [_jsx(Grid, __assign({ item: true, xs: 6 }, { children: _jsx(FormField, __assign({ name: "validation", label: t('mfa.form.validation'), control: control }, { children: _jsx(Autocomplete, { options: MFAValidationOptions }) })) })), _jsx(Grid, __assign({ item: true, xs: 6 }, { children: validation === MFAValidationType.SecretKey ? (_jsx(FormField, __assign({ required: true, name: "secretKey", label: t('mfa.form.secretKey'), control: control }, { children: _jsx(TextField, { disabled: true }) }))) : (_jsx(Box, __assign({ display: "flex", justifyContent: "center" }, { children: _jsx(QRCode, { value: "otpauth://totp/cinareo-".concat(userData === null || userData === void 0 ? void 0 : userData.email.split('@')[0], "?secret=").concat(secretKey !== null && secretKey !== void 0 ? secretKey : '') }) }))) }))] })), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsx(FormField, __assign({ required: true, name: "userCode", label: t('mfa.form.authenticatorCode'), control: control }, { children: _jsx(PatternFormat, { customInput: TextField, format: "######" }) })) })), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsx(FormField, __assign({ name: "friendlyDeviceName", label: t('mfa.form.deviceName'), control: control }, { children: _jsx(TextField, { inputProps: { maxLength: 255 } }) })) }))] })), type === MFAType.SMS && (_jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsx(FormField, __assign({ required: true, name: "phone", label: t('mfa.form.phoneNumber'), control: control }, { children: _jsx(PhoneInput, {}) })) })))] }))] }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: onClose }, { children: t('action.cancel') })), _jsx(Button, __assign({ variant: "contained", type: "submit" }, { children: t('action.ok') }))] })] }))] })), _jsx(VerifyPhoneNumber, { open: openVerifyPhoneNumber, onClose: function () { return setOpenVerifyPhoneNumber(false); }, phone: phone, onSuccess: onClose })] })));
};
